import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CARD_SECTIONS, type CardDto, type StreamDto } from '../lib/types';
import { boardQueryKey, getBoard } from '../lib/workApi';
import { formatMoney, formatShortDate } from '../lib/format';
import { buildCost } from '../lib/roi';
import { monthInfo } from '../lib/months';

// Document colours (org scheme): blue, orange (rules only, never text), charcoal.
const BLUE = '#214592';
const ORANGE = '#dd6e0c';
const CHARCOAL = '#444444';

const pageCss = `
@page { size: letter landscape; margin: 0.5in; }
@media print {
  html, body { background: #fff; }
  .wp-table { break-inside: auto; }
  .wp-table tr { break-inside: avoid; }
  .wp-band { break-after: avoid; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

function printDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function orderedCards(stream: StreamDto): CardDto[] {
  return [...stream.cards].sort(
    (a, b) =>
      CARD_SECTIONS.indexOf(a.section) - CARD_SECTIONS.indexOf(b.section) || a.sortOrder - b.sortOrder,
  );
}

const dash = (v: string | null | undefined) => (v === null || v === undefined || v.trim() === '' ? '-' : v);

const COLUMNS = ['Title', 'Section', 'Month', 'Owner', 'One-time', 'Per year', 'Build cost', 'Workers', 'Weeks', 'Expected', 'Depends on'];
const MONEY_COLUMNS = new Set(['One-time', 'Per year', 'Build cost']);

const money = (n: number | null | undefined) => (n === null || n === undefined ? '-' : formatMoney(n));

function roiTotals(cards: readonly CardDto[]): { oneTime: number; annual: number; cost: number } {
  return cards.reduce(
    (t, c) => ({
      oneTime: t.oneTime + (c.roiOneTime ?? 0),
      annual: t.annual + (c.roiAnnual ?? 0),
      cost: t.cost + buildCost(c),
    }),
    { oneTime: 0, annual: 0, cost: 0 },
  );
}

/** Build cost cell: "-" when neither cost field is set. */
const costCell = (c: CardDto) => (c.tokensCost === null && c.additionalCost === null ? '-' : formatMoney(buildCost(c)));

const totalRowStyle = { fontWeight: 700, borderTop: `1pt solid ${BLUE}` } as const;

/**
 * Print-to-PDF view of the board (route `/print`, rendered outside the app shell). Opens the
 * browser print dialog once the board has loaded.
 */
export function WorkPrint() {
  const boardQ = useQuery({ queryKey: boardQueryKey, queryFn: getBoard });
  const printed = useRef(false);

  useEffect(() => {
    if (!boardQ.data) return;
    // Let the table paint before the dialog opens. The flag is set inside the callback so a
    // StrictMode effect re-run (cleanup clears the first timer) still prints exactly once.
    const timer = window.setTimeout(() => {
      if (printed.current) return;
      printed.current = true;
      window.print();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [boardQ.data]);

  if (boardQ.isLoading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (boardQ.isError || !boardQ.data) {
    return <div className="p-6 text-sm text-red-700" role="alert">Couldn't load the work board.</div>;
  }

  const streams = boardQ.data.streams;
  const titleById = new Map(streams.flatMap((s) => s.cards).map((c) => [c.id, c.title]));
  const grand = roiTotals(streams.flatMap((s) => s.cards));

  return (
    <div className="bg-white min-h-screen p-6 print:p-0" style={{ color: CHARCOAL }}>
      <style>{pageCss}</style>
      <p className="print:hidden mb-4 text-sm text-slate-600 border border-slate-200 rounded px-3 py-2 bg-slate-50">
        Choose 'Save as PDF' in the print dialog — Landscape
      </p>

      <header className="flex items-baseline justify-between pb-1" style={{ borderBottom: `2pt solid ${ORANGE}` }}>
        <h1 style={{ color: BLUE, fontSize: '16pt', fontWeight: 700 }}>Thunderdome: Work Board</h1>
        <span style={{ color: CHARCOAL, fontSize: '9pt' }}>{printDate(new Date())}</span>
      </header>

      {streams.length === 0 && <p className="mt-4" style={{ fontSize: '10pt' }}>No work streams yet.</p>}

      {streams.map((stream) => {
        const cards = orderedCards(stream);
        const totals = roiTotals(cards);
        return (
          <section key={stream.id} style={{ marginTop: '10pt' }}>
            <div
              className="wp-band flex gap-3 px-2 py-1"
              style={{ background: BLUE, color: '#ffffff', fontSize: '10pt', fontWeight: 700 }}
            >
              <span>{stream.name}</span>
              <span>
                {stream.usedWorkers} of {stream.workerCount} used
              </span>
            </div>
            <table className="wp-table w-full border-collapse" style={{ fontSize: '8pt', color: CHARCOAL }}>
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col}
                      className={`${MONEY_COLUMNS.has(col) ? 'text-right' : 'text-left'} px-2 py-1`}
                      style={{ fontWeight: 700, borderBottom: `1pt solid ${ORANGE}` }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cards.length === 0 ? (
                  <tr style={{ borderBottom: '0.5pt solid #d4d4d4' }}>
                    {COLUMNS.map((col) => (
                      <td key={col} className="px-2 py-1">-</td>
                    ))}
                  </tr>
                ) : (
                  cards.map((card) => (
                    <tr key={card.id} style={{ borderBottom: '0.5pt solid #d4d4d4' }}>
                      <td className="px-2 py-1">{dash(card.title)}</td>
                      <td className="px-2 py-1">{card.section}</td>
                      <td className="px-2 py-1 whitespace-nowrap">
                        {monthInfo(card.targetMonth) ? (
                          <span
                            className="inline-block rounded-full px-1.5 leading-tight"
                            style={{ background: monthInfo(card.targetMonth)!.color, color: monthInfo(card.targetMonth)!.text, fontWeight: 600 }}
                          >
                            {monthInfo(card.targetMonth)!.short}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-2 py-1">{dash(card.productOwnerName)}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-right">{money(card.roiOneTime)}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-right">{money(card.roiAnnual)}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-right">{costCell(card)}</td>
                      <td className="px-2 py-1">{card.workersNeeded}</td>
                      <td className="px-2 py-1">{card.estimateWeeks}</td>
                      <td className="px-2 py-1 whitespace-nowrap">{card.expectedDate ? formatShortDate(card.expectedDate) : '-'}</td>
                      <td className="px-2 py-1">
                        {dash(
                          card.dependencyCardId != null
                            ? (titleById.get(card.dependencyCardId) ?? card.dependencyCardTitle)
                            : null,
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr style={totalRowStyle}>
                  <td className="px-2 py-1" colSpan={4}>Total</td>
                  <td className="px-2 py-1 whitespace-nowrap text-right">{formatMoney(totals.oneTime)}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-right">{formatMoney(totals.annual)}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-right">{formatMoney(totals.cost)}</td>
                  <td className="px-2 py-1" colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </section>
        );
      })}

      {streams.length > 0 && (
        <div
          className="wp-band flex justify-end gap-6 px-2 py-1"
          style={{ marginTop: '10pt', borderTop: `2pt solid ${ORANGE}`, color: BLUE, fontSize: '10pt', fontWeight: 700 }}
        >
          <span>All streams</span>
          <span>One-time: {formatMoney(grand.oneTime)}</span>
          <span>Per year: {formatMoney(grand.annual)}</span>
          <span>Build cost: {formatMoney(grand.cost)}</span>
        </div>
      )}
    </div>
  );
}
